import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type RefObject,
  type WheelEvent,
} from 'react';
import {
  buildHorizontalScrollEdgeMaskStyle,
  HORIZONTAL_SCROLL_EDGE_FADE_PX,
} from '@/lib/horizontal-scroll-edge-mask';

export type UseHorizontalScrollEdgeFadesOptions = {
  /** When false, skips listeners and returns no mask (e.g. welcome layout). */
  active?: boolean;
  fadePx?: number;
  /** Re-run edge measurement when this changes (e.g. pill count). */
  measureKey?: unknown;
};

/**
 * Horizontal overflow edge fades + wheel-to-scroll for a single-axis scrollport.
 * Used by the in-chat capability pill tray above the composer.
 */
export function useHorizontalScrollEdgeFades(
  scrollRef: RefObject<HTMLElement | null>,
  options: UseHorizontalScrollEdgeFadesOptions = {},
) {
  const { active = true, fadePx = HORIZONTAL_SCROLL_EDGE_FADE_PX, measureKey } = options;
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateEdgeFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    const nextLeft = hasOverflow && !atStart;
    const nextRight = hasOverflow && !atEnd;
    setShowLeftFade((prev) => (prev === nextLeft ? prev : nextLeft));
    setShowRightFade((prev) => (prev === nextRight ? prev : nextRight));
  }, [scrollRef]);

  /** Classic mice only emit deltaY — map vertical wheel to horizontal scroll when overflowing. */
  const onWheel = useCallback(
    (event: WheelEvent<HTMLElement>) => {
      const el = scrollRef.current;
      if (!el || el.scrollWidth <= el.clientWidth + 1) return;

      const delta = event.deltaY + event.deltaX;
      if (delta === 0) return;

      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      const scrollingForward = delta > 0;
      const scrollingBack = delta < 0;

      if ((scrollingForward && !atEnd) || (scrollingBack && !atStart)) {
        event.preventDefault();
        el.scrollLeft += delta;
      }
    },
    [scrollRef],
  );

  useEffect(() => {
    if (!active) return;

    updateEdgeFades();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateEdgeFades, { passive: true });
    const observer = new ResizeObserver(updateEdgeFades);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateEdgeFades);
      observer.disconnect();
    };
  }, [active, measureKey, scrollRef, updateEdgeFades]);

  const maskStyle: CSSProperties | undefined = useMemo(
    () => buildHorizontalScrollEdgeMaskStyle(showLeftFade, showRightFade, fadePx),
    [fadePx, showLeftFade, showRightFade],
  );

  return { maskStyle, onWheel, showLeftFade, showRightFade };
}
